import { ChildComponent, ComponentIds, DisplayNameComponent, FunctionalMarginShapeComponent, HierarchyUtils, LocalTransformComponent, Margin, Ray, RaycastUtils, SnapshotIgnoreComponent, Transform, TransformUtils, Vector } from '@inter-ikea-core/core';
import { PlannerEcsSystem } from '../System';
import { getLineCenter, getLineLength, getRotation } from './HelpLinesUtils';
import HelpLinesBoxColliderComponent from '../../components/HelpLinesBoxColliderComponent';
import HelpLinesColliderBoxesComponent from '../../components/HelpLinesColliderBoxesComponent';
export class HelpLinesColliderBoxSystem extends PlannerEcsSystem {
    constructor(coreApi) {
        super(coreApi, 'HelpLinesColliderBoxSystem', HelpLinesColliderBoxesComponent.id);
        /**
         * Creates collider boxes based on the shape dimensions.
         * @param component - The component containing dimensions.
         */
        this.createColliderBoxes = (component) => {
            this.colliderBoxes = component.dimensions.reduce((agg, corner, i) => {
                // We do not want to create the last colliding box since it will be on the floor and not necessary.
                if (i === component.dimensions.length - 1)
                    return agg;
                const { size, position, rotation } = this.getColliderBoxProps(i, component.dimensions);
                agg.push(this.createCollidingBox(size, position, rotation, this.coreApi));
                return agg;
            }, []);
        };
        /**
         * Gets the properties for a collider box.
         * @param currentCornerIndex - The index of the current corner.
         * @param dimensions - The dimensions of the shape.
         * @returns An object containing size, position, and rotation of the collider box.
         */
        this.getColliderBoxProps = (currentCornerIndex, dimensions) => {
            const position = dimensions[currentCornerIndex];
            const nextCorner = dimensions[currentCornerIndex !== dimensions.length - 1 ? currentCornerIndex + 1 : 0];
            const lineCenter = getLineCenter(position, nextCorner);
            const lineLength = getLineLength(position, nextCorner);
            const rotation = getRotation(position, nextCorner);
            const scene = this.coreApi.entities.getScene();
            if (scene) {
                const parent = HierarchyUtils.parent(scene);
                if (!parent)
                    return { size: new Vector(0, 0, 0), position: new Vector(0, 0, 0), rotation };
                const ray = new Ray(lineCenter, new Transform(new Vector(position.X, position.Y, position.Z), rotation).right.normalize());
                const outerHit = RaycastUtils.raycast(ray, scene, null, [
                    ComponentIds.ROOM_PART_COMPONENT_ID
                ]);
                return {
                    size: new Vector(outerHit ? outerHit.distance : 4000, lineLength, 10), // X is width of collider box. The width is the distance from the help line outwards (towards a wall or ceiling), Y is the same as the length of the line from current corner to the next, Z is depth of box
                    position: new Vector(lineCenter.X, lineCenter.Y, lineCenter.Z),
                    rotation
                };
            }
            return { size: new Vector(0, 0, 0), position: new Vector(0, 0, 0), rotation };
        };
        /**
         * Creates a colliding box entity.
         * @param size - The size of the box.
         * @param position - The position of the box.
         * @param rotation - The rotation of the box.
         * @param coreApi - The core API instance.
         * @returns The created colliding box entity.
         */
        this.createCollidingBox = (size, position, rotation, coreApi) => {
            const coreServices = coreApi.getServices();
            const colliderBox = coreServices.entityManager.createEntity();
            colliderBox.addComponent(new DisplayNameComponent('Box collider'));
            colliderBox.addComponent(new HelpLinesBoxColliderComponent());
            colliderBox.addComponent(new LocalTransformComponent(new Transform(new Vector(position.X, position.Y, size.Z / 2 + position.Z))));
            colliderBox.addComponent(new ChildComponent(coreServices.sceneManager.scene));
            const transform = TransformUtils.localTransform(colliderBox);
            const newTransform = new Transform(transform.position, rotation);
            TransformUtils.setWorldTransform(colliderBox, newTransform);
            const entityWorldTransform = TransformUtils.worldTransform(colliderBox);
            const movement = entityWorldTransform.right.times(size.X / 2 + 5);
            TransformUtils.moveInParentSpace(colliderBox, movement);
            colliderBox.addComponent(new FunctionalMarginShapeComponent(new Margin(size.Y / 2, size.X / 2, size.Y / 2, size.X / 2, size.Z / 2, size.Z / 2)));
            colliderBox.addComponent(new SnapshotIgnoreComponent());
            return colliderBox;
        };
        this.coreApi = coreApi;
        this.functionalMarginVisualizationSystem = null;
        this.colliderBoxes = [];
        this.transparentMaterialId = -1;
    }
    /**
     * Called when a new component is added to an entity.
     * @param entity - The entity to which the component is added.
     * @param newComponent - The new component being added.
     */
    onAdd(entity, newComponent) {
        this.removeColliderBoxes();
        this.createColliderBoxes(newComponent);
    }
    /**
     * Called when a component is updated.
     * @param entity - The entity to which the component belongs.
     * @param oldComponent - The old component before the update.
     * @param newComponent - The new component after the update.
     */
    onUpdate(entity, oldComponent, newComponent) {
        this.removeColliderBoxes();
        if (newComponent.dimensions.length > 0) {
            this.createColliderBoxes(newComponent);
        }
    }
    /**
     * Called when a component is removed from an entity.
     */
    onRemove() {
        this.removeColliderBoxes();
    }
    /**
     * Removes all collider boxes.
     */
    removeColliderBoxes() {
        const coreServices = this.coreApi.getServices();
        this.colliderBoxes.forEach(box => {
            if (!box.isDisposed)
                coreServices.entityManager.disposeEntity(box);
        });
        this.colliderBoxes = [];
    }
    /**
     * Get the back wall dimensions to local variable.
     * @returns The dimensions of the back wall.
     */
    getWallDimensions() {
        const wall = this.coreApi.camera.getWallFacingDirection(new Vector(0, 0, -1));
        if (!wall)
            return { minX: 0, maxX: 0, maxY: 0, z: 0 };
        const worldDragSurfacesComponent = wall.getComponent(ComponentIds.WORLD_DRAG_SURFACES_COMPONENT_ID);
        const axisAlignedBoundingBox = worldDragSurfacesComponent?.surfaces[0].surface.axisAlignedBoundingBox;
        const height = this.coreApi.kvadratRoom?.getRoomSize()?.height;
        if (!axisAlignedBoundingBox)
            return { minX: 0, maxX: 0, maxY: 0, z: 0 };
        return {
            minX: axisAlignedBoundingBox.min.X / 1000,
            maxX: axisAlignedBoundingBox.max.X / 1000,
            maxY: height ? height / 1000 : 0,
            z: axisAlignedBoundingBox.min.Z / 1000
        };
    }
}
//# sourceMappingURL=HelpLinesColliderBoxSystem.js.map